import { SessionResponseSchema } from '@gestion-permisos/contracts';
import type { z } from 'zod';
import { parseContract } from './contract.js';
import { ApiProblem } from './problem.js';

/** Qué hacer si la API responde 401. */
export type UnauthorizedPolicy =
  /** Arranque: navegación de página completa al inicio de sesión con la ruta actual como `returnTo`. */
  | 'login'
  /** Durante el uso (cualquier otro 401): pantalla de sesión expirada. */
  | 'expired';

export type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClientOptions {
  /** `true` en desarrollo y pruebas: las respuestas deben cumplir el contrato estricto. */
  readonly strictContracts: boolean;
  /** Ruta actual de la aplicación (`pathname` + `search`), para `returnTo`. */
  readonly currentPath: () => string;
  /** Navegación de página completa al inicio de sesión del BFF. */
  readonly onLoginRequired: (returnTo: string) => void;
  /** Navegación a la pantalla de sesión expirada (con la ruta en la que estaba el usuario). */
  readonly onSessionExpired: (returnTo: string) => void;
  readonly fetch?: typeof fetch;
}

interface RequestOptions<T> {
  readonly schema?: z.ZodType<T>;
  readonly onUnauthorized?: UnauthorizedPolicy;
  /** Cabeceras de la operación (`If-Match`, `Idempotency-Key`). Se repiten igual en el reintento por CSRF. */
  readonly headers?: Readonly<Record<string, string>>;
}

const API_PREFIX = '/api/v1/';

/**
 * Nombre de archivo de `Content-Disposition` (`filename="…"` o `filename*=UTF-8''…`). Solo el nombre: sin
 * rutas ni caracteres de control. Sin nombre válido, `undefined`.
 */
export function fileNameFrom(header: string | null): string | undefined {
  if (header === null) return undefined;
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)?.[1];
  let name: string | undefined;
  if (encoded !== undefined) {
    try {
      name = decodeURIComponent(encoded.trim());
    } catch {
      name = undefined;
    }
  }
  name ??= /filename\s*=\s*"([^"]*)"/i.exec(header)?.[1] ?? /filename\s*=\s*([^;\s]+)/i.exec(header)?.[1];
  // Sin caracteres de control (U+0000–U+001F y U+007F).
  const printable = (text: string) => [...text].filter((char) => char.charCodeAt(0) >= 0x20 && char.charCodeAt(0) !== 0x7f).join('');
  const last = name?.split(/[\\/]/).pop();
  const base = last === undefined ? undefined : printable(last).trim();
  return base ? base : undefined;
}

const SESSION_PATH = '/api/v1/auth/session';
const CSRF_HEADER = 'X-CSRF-Token';

/**
 * Cliente de la API (plan 11 §4.2). Solo habla con la API, por rutas relativas `/api/v1/…`, con la cookie
 * del BFF (`credentials: 'include'`). Ningún token de Entra pasa por el navegador.
 *
 * Token CSRF: `GET /auth/session` lo rota en cada llamada y solo vale el último, así que se pide una sola
 * vez, se guarda **solo en memoria** y las peticiones simultáneas comparten la misma promesa. Si una
 * mutación responde 403 `CSRF_INVALID` (p. ej., otra pestaña lo rotó), se pide otro y se reintenta una
 * sola vez: la guardia de la API rechaza la petición antes de ejecutar el caso de uso.
 */
export class ApiClient {
  private csrfToken: string | undefined;
  private csrfRequest: Promise<string> | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  }

  get<T>(path: string, schema: z.ZodType<T>, options: { onUnauthorized?: UnauthorizedPolicy } = {}): Promise<T> {
    return this.request<T>('GET', path, undefined, { ...options, schema });
  }

  /**
   * `POST`, `PUT`, `PATCH` o `DELETE` con `X-CSRF-Token`. Un cuerpo `Blob` (un archivo) se envía tal cual, con
   * su tipo en `Content-Type`; cualquier otro, como JSON.
   */
  async mutate<T = undefined>(method: MutationMethod, path: string, body?: unknown, options: RequestOptions<T> = {}): Promise<T> {
    const token = await this.csrf();
    try {
      return await this.request<T>(method, path, body, options, token);
    } catch (error) {
      if (!(error instanceof ApiProblem) || error.status !== 403 || error.code !== 'CSRF_INVALID') throw error;
      // Un solo reintento. Si otra mutación ya renovó el token, se usa ese sin rotarlo otra vez.
      if (this.csrfToken === token) this.forgetCsrf();
      return this.request<T>(method, path, body, options, await this.csrf());
    }
  }

  /**
   * Descarga un archivo de la API (`GET`, con la cookie de sesión): el cuerpo se devuelve tal cual, **sin
   * interpretarlo como JSON**, con el nombre de `Content-Disposition`. Los errores son Problem Details,
   * como en el resto del cliente (un 401 lleva a la sesión expirada).
   */
  async download(path: string): Promise<{ blob: Blob; fileName: string | undefined }> {
    if (!path.startsWith(API_PREFIX)) throw new Error('El cliente solo admite rutas relativas de la API (/api/v1/…).');
    const response = await this.fetchImpl(path, { method: 'GET', credentials: 'include' });
    if (!response.ok) {
      const problem = await ApiProblem.from(response);
      if (response.status === 401) this.handleUnauthorized('expired');
      throw problem;
    }
    return { blob: await response.blob(), fileName: fileNameFrom(response.headers.get('content-disposition')) };
  }

  /** Olvida el token CSRF (tras cerrar la sesión). */
  forgetCsrf(): void {
    this.csrfToken = undefined;
  }

  private csrf(): Promise<string> {
    if (this.csrfToken !== undefined) return Promise.resolve(this.csrfToken);
    this.csrfRequest ??= this.get(SESSION_PATH, SessionResponseSchema)
      .then((session) => {
        this.csrfToken = session.csrfToken;
        return session.csrfToken;
      })
      .finally(() => {
        this.csrfRequest = undefined;
      });
    return this.csrfRequest;
  }

  private async request<T>(method: string, path: string, body: unknown, options: RequestOptions<T>, csrfToken?: string): Promise<T> {
    if (!path.startsWith(API_PREFIX)) throw new Error('El cliente solo admite rutas relativas de la API (/api/v1/…).');
    const headers: Record<string, string> = { ...options.headers, Accept: 'application/json' };
    if (csrfToken !== undefined) headers[CSRF_HEADER] = csrfToken;
    const binary = body instanceof Blob;
    if (body !== undefined) headers['Content-Type'] = binary ? body.type || 'application/octet-stream' : 'application/json';
    const response = await this.fetchImpl(path, {
      method,
      credentials: 'include',
      headers,
      ...(body === undefined ? {} : { body: binary ? body : JSON.stringify(body) }),
    });

    if (!response.ok) {
      const problem = await ApiProblem.from(response);
      if (response.status === 401) this.handleUnauthorized(options.onUnauthorized ?? 'expired');
      throw problem;
    }
    if (options.schema === undefined || response.status === 204) return undefined as T;
    return parseContract(options.schema, await response.json(), this.options.strictContracts);
  }

  private handleUnauthorized(policy: UnauthorizedPolicy): void {
    this.forgetCsrf();
    if (policy === 'login') this.options.onLoginRequired(this.options.currentPath());
    else this.options.onSessionExpired(this.options.currentPath());
  }
}
